import React, { useMemo, useState } from 'react';
import { Box, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { findSystems, fetchFindOptions, SystemFindResult } from '../api/systems';
import ConsolePanel from '../components/ConsolePanel';
import StatusBadge from '../components/StatusBadge';
import { C, FONT, classColor, frameSx, hexToRgba, lcarsLabelSx, revealSx } from '../theme/tokens';

/**
 * Find systems by what they ARE, not by what they are called.
 *
 * Search could only ever match a name someone already knew, so the corp's
 * standing questions — "find us a C3 with a highsec static", "which Pulsar
 * C5s do we know", "where can we mine Arkonor" — were answered by leaving
 * the app for anoik.is and reading a table there.
 *
 * This is deliberately the thing a chain mapper cannot do. Pathfinder knows
 * the systems you have personally scanned, which is a handful; this queries
 * the whole curated catalogue — statics, effects and ore/ice variants across
 * every catalogued w-space system — so it answers questions about systems
 * nobody has scanned yet. Intel depth, not mapping (see PRODUCT.md).
 */

/** The effect keys are stored snake_case; nobody says "wolf_rayet" out loud. */
const prettyEffect = (key: string) =>
  key
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

type FilterKey = 'class' | 'static' | 'effect' | 'resource';

const FILTER_LABELS: Record<FilterKey, string> = {
  class: 'Class',
  static: 'Static leads to',
  effect: 'Effect',
  resource: 'Ore / ice',
};

const Find: React.FC = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<Partial<Record<FilterKey, string>>>({});

  const options = useQuery({ queryKey: ['systems', 'find-options'], queryFn: fetchFindOptions });

  const active = useMemo(
    () => Object.fromEntries(Object.entries(filters).filter(([, v]) => !!v)) as Record<string, string>,
    [filters],
  );
  const hasFilters = Object.keys(active).length > 0;

  const results = useQuery({
    queryKey: ['systems', 'find', active],
    queryFn: () => findSystems(active),
    // Without a filter this is just "the whole catalogue, alphabetically",
    // which is a worse answer than asking the question properly.
    enabled: hasFilters,
  });

  const toggle = (key: FilterKey, value: string) =>
    setFilters((prev) => ({ ...prev, [key]: prev[key] === value ? undefined : value }));

  const chipRow = (key: FilterKey, values: string[], render: (v: string) => string = (v) => v) => (
    <Stack key={key} spacing={0.75}>
      <Typography sx={lcarsLabelSx(C.textDim, '0.7rem')}>{FILTER_LABELS[key]}</Typography>
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
        {values.map((value) => {
          const selected = filters[key] === value;
          return (
            <Chip
              key={value}
              label={render(value)}
              size="small"
              onClick={() => toggle(key, value)}
              sx={{
                cursor: 'pointer',
                fontFamily: FONT.mono,
                fontSize: '0.72rem',
                color: selected ? C.bg : C.text,
                backgroundColor: selected ? C.cyan : hexToRgba(C.cyan, 0.08),
                border: `1px solid ${hexToRgba(C.cyan, selected ? 0.9 : 0.25)}`,
                '&:hover': { backgroundColor: selected ? C.cyan : hexToRgba(C.cyan, 0.18) },
              }}
            />
          );
        })}
      </Stack>
    </Stack>
  );

  return (
    <Stack spacing={3} sx={revealSx()}>
      <ConsolePanel title="Find Systems" accent={C.cyan}>
        <Typography sx={{ color: C.textDim, fontSize: '0.84rem', mb: 2 }}>
          Search the catalogue by what a system <em>is</em> — its class, where its statics go, its effect, and what it
          holds. Covers every catalogued wormhole, not just the ones the corp has scanned.
        </Typography>

        {options.isLoading && <CircularProgress size={20} sx={{ color: C.cyan }} />}
        {options.isError && (
          <Typography sx={{ color: C.gold, fontSize: '0.82rem' }}>
            Filter options unavailable right now — try again in a moment.
          </Typography>
        )}

        {options.data && (
          <Stack spacing={2}>
            {chipRow('class', options.data.classes)}
            {chipRow('static', options.data.statics)}
            {chipRow('effect', options.data.effects, prettyEffect)}
            {chipRow(
              'resource',
              options.data.resources.map((r) => r.value),
            )}
            {hasFilters && (
              <Box>
                <Chip
                  label="Clear all"
                  size="small"
                  onClick={() => setFilters({})}
                  sx={{ cursor: 'pointer', color: C.textDim, fontSize: '0.72rem' }}
                />
              </Box>
            )}
          </Stack>
        )}
      </ConsolePanel>

      {hasFilters && (
        <ConsolePanel title="Matches" accent={C.wormhole}>
          {results.isLoading && <CircularProgress size={20} sx={{ color: C.wormhole }} />}
          {results.isError && (
            <Typography sx={{ color: C.gold, fontSize: '0.84rem' }}>
              Search failed — try again, or narrow the filters.
            </Typography>
          )}
          {results.data && results.data.items.length === 0 && (
            <Typography sx={{ color: C.textDim, fontSize: '0.84rem' }}>
              No catalogued system matches all of those. Try dropping a filter.
            </Typography>
          )}
          {results.data && results.data.items.length > 0 && (
            <Stack spacing={1}>
              {/* Say when the list is a slice: "50 of 214" reads very
                  differently from a bare 50, and someone picking a target
                  needs to know they are not seeing the whole field. */}
              <Typography sx={{ color: C.textDim, fontFamily: FONT.mono, fontSize: '0.74rem' }}>
                {results.data.items.length === results.data.total
                  ? `${results.data.total} ${results.data.total === 1 ? 'system' : 'systems'}`
                  : `Showing ${results.data.items.length} of ${results.data.total} matches`}
              </Typography>
              {results.data.items.map((system) => (
                <FindRow key={system.solar_system_id} system={system} onOpen={() => navigate(`/system/${system.name}`)} />
              ))}
            </Stack>
          )}
        </ConsolePanel>
      )}
    </Stack>
  );
};

const FindRow: React.FC<{ system: SystemFindResult; onOpen: () => void }> = ({ system, onOpen }) => (
  <Stack
    component="button"
    type="button"
    onClick={onOpen}
    direction="row"
    alignItems="center"
    spacing={1.25}
    flexWrap="wrap"
    useFlexGap
    sx={{
      ...frameSx(C.wormhole),
      textAlign: 'left',
      appearance: 'none',
      cursor: 'pointer',
      width: '100%',
      p: 1,
      transition: 'transform 0.15s ease',
      '&:hover': { transform: 'translateY(-1px)' },
      '&:focus-visible': { outline: `2px solid ${C.cyan}`, outlineOffset: '2px' },
    }}
  >
    <Typography sx={{ color: classColor(system.system_class), fontFamily: FONT.mono, fontWeight: 700, minWidth: 96 }}>
      {system.name}
    </Typography>
    <StatusBadge label={system.system_class} accent={classColor(system.system_class)} />
    {system.statics.map((s) => (
      <StatusBadge key={s.code} label={`${s.code} → ${s.leadsTo}`} accent={C.cyan} dim />
    ))}
    {system.effect && <StatusBadge label={prettyEffect(system.effect)} accent={C.gold} />}
  </Stack>
);

export default Find;
